import { OtpSignInPasswordForm } from "@circle-react/components/OnboardingPage/OtpSignInPage/OtpSignInPasswordForm";
import { OtpChallengeContent } from "@circle-react/components/OtpChallenge/OtpChallengeContent";
import type { Variant } from "@circle-react/contexts/OtpChallengeContext";
import { Modal } from "@circleco/compass/components/Modal";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

interface OtpChallengeModalCompassProps {
  email?: string;
  error?: boolean | null;
  isLoading?: boolean;
  isOpen: boolean;
  isPasswordStep?: boolean;
  onCancel: () => void;
  onShowOtpForm?: () => void;
  onSignInWithPassword?: () => void;
  onSubmit: (otpCode: string) => void | Promise<void>;
  showSignInWithCode?: boolean;
  showSignInWithPassword?: boolean;
  variant: Variant;
}

export const OtpChallengeModalCompass = ({
  isOpen,
  variant,
  email,
  onSubmit,
  onCancel,
  isLoading,
  error,
  showSignInWithPassword = false,
  onSignInWithPassword,
  isPasswordStep = false,
  onShowOtpForm,
  showSignInWithCode = true,
}: OtpChallengeModalCompassProps) => {
  // Toast must be called outside Modal.Root: useToast() from Compass throws
  // when called inside Modal.Root children (they shadow the CompassProvider context).
  const toast = useToast();

  const isFullscreen = variant === "modal-fullscreen";

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={open => !open && !isLoading && onCancel()}
      size={isFullscreen ? "full-screen" : "md"}
    >
      <Modal.Content>
        {!isPasswordStep && email && (
          <Modal.Header title={email} showCloseButton />
        )}
        <Modal.Body>
          {isPasswordStep && email ? (
            <OtpSignInPasswordForm
              email={email}
              onShowOtpForm={onShowOtpForm ?? (() => undefined)}
              onCancel={onCancel}
              showSignInWithCode={showSignInWithCode}
              variant={variant}
            />
          ) : (
            <OtpChallengeContent
              email={email}
              isLoading={isLoading}
              error={error}
              onCancel={onCancel}
              onSubmit={onSubmit}
              onResendSuccess={message => toast.success(message)}
              onResendError={message =>
                toast.error(message || t("otp_challenge_modal.resend_failed"))
              }
              showTitle={false}
              showHeader={false}
              showSubheaderTitle
              includeBorder={false}
              showSignInWithPassword={showSignInWithPassword}
              onSignInWithPassword={onSignInWithPassword}
            />
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

OtpChallengeModalCompass.displayName = "OtpChallengeModalCompass";
